/**
 * The shipped default bindings, plus the pure functions that turn defaults +
 * user overrides into the keymap the binder actually registers.
 *
 * Nothing here touches React, the DOM or storage — it is all data and pure
 * transforms, which is why the conflict rules and the `Mod` normalization are
 * testable without a browser (`keymap.test.ts`).
 *
 * Every default is written with `Mod+` rather than `Control+`/`Meta+`. `Mod`
 * resolves to Command on macOS and Control elsewhere, so one entry is right on
 * every platform — and, just as importantly, two bindings that differ only in
 * that choice must be recognised as the SAME chord when hunting conflicts,
 * which is what `bindingKey` is for.
 */
// Imported from the react adapter, not `@tanstack/hotkeys` — see the note in
// `types.ts` for why the core package is never a direct dependency here.
import {
  detectPlatform,
  hasNonModifierKey,
  normalizeRegisterableHotkey,
  parseHotkey,
} from '@tanstack/react-hotkeys';
import { COMMANDS, commandsById, scopeOf } from './catalog';
import {
  isSequenceBinding,
  type Binding,
  type CommandId,
  type CommandScope,
  type Keymap,
  type KeymapOverrides,
  type Platform,
} from './types';

/**
 * The shipped bindings. Total by construction — see `Keymap`.
 *
 * A command may carry more than one binding (an alias). The first is the one
 * the UI shows; the rest still fire.
 */
export const DEFAULT_KEYMAP: Keymap = {
  // `?` would be the conventional key, and it is deliberately NOT used: `?` is
  // `Shift+/` on a US layout and something else entirely elsewhere, so the
  // `Hotkey` type excludes every `Shift+<punctuation>` combination on purpose.
  // Binding it would give a shortcut that works on the keyboard it was written
  // on. `Mod+/` carries no such ambiguity.
  'general.showShortcuts': ['Mod+/'],
  'general.toggleAssistant': ['Mod+Shift+A'],
  'general.closeOverlay': ['Escape'],

  // Vim-style `g <where>` prefixes. Two steps rather than a chord keeps the
  // whole navigation set out of the modifier space the browser already owns.
  'navigation.goHome': [['G', 'H']],
  'navigation.goMeshgen': [['G', 'M']],
  'navigation.goSimops': [['G', 'S']],

  // Bare letters, scoped to the viewer. Safe as single keys because the
  // shape-based `ignoreInputs` default keeps them out of text fields — and
  // chosen to miss the renderer's own reserved set (see the design-points note
  // below): it owns x/y/z, i/j/k/l, o, u, v, f, c and Space on its canvas.
  'viewer.resetCamera': ['R'],

  // Arrow keys only, and no `J`/`K` Vim aliases. The mesh renderer owns its own
  // `j`/`k` (and `i`/`l`/`o`/`u`/`v`/`f`/`c`/`x`/`y`/`z`/Space) on its canvas,
  // outside this system entirely — so a collision there is invisible to
  // `findKeymapConflicts`, which can only see commands it knows about. Until
  // the renderer's keys move into the catalog, treat its set as reserved.
  'designPoints.selectNext': ['ArrowDown'],
  'designPoints.selectPrevious': ['ArrowUp'],
  'designPoints.closeDetail': ['Escape'],

  'reportWriteup.save': ['Mod+S'],
};

/**
 * Defaults with the user's overrides applied.
 *
 * An id present in `overrides` wins outright, including when its value is an
 * empty array — that is how a user unbinds a command, and it must not fall
 * back to the default.
 */
export function resolveKeymap(overrides: KeymapOverrides): Keymap {
  const resolved: Record<string, readonly Binding[]> = { ...DEFAULT_KEYMAP };
  for (const [id, bindings] of Object.entries(overrides)) {
    if (bindings) resolved[id] = bindings;
  }
  return resolved as Keymap;
}

/**
 * A platform-stable identity for a binding, used to decide whether two
 * bindings are the same chord.
 *
 * The normalization is the whole value here: on Windows `Mod+S` and
 * `Control+S` are one chord and must collide, while on macOS `Mod+S` collides
 * with `Meta+S` instead. Comparing the raw strings would miss both and let a
 * genuine double-binding ship.
 *
 * Sequences and chords live in separate namespaces — `['G','H']` cannot
 * collide with a chord — but a sequence's steps are each normalized, so
 * `['Mod+G','H']` and `['Control+G','H']` collide on Windows.
 */
export function bindingKey(binding: Binding, platform?: Platform): string {
  const target = platform ?? detectPlatform();
  if (isSequenceBinding(binding)) {
    // `\u0000` would be the natural joiner and is banned here: NUL bytes in a
    // source file make git classify it as binary, which this module learned the
    // hard way. `|` cannot appear in a normalized hotkey (every token is a
    // modifier name, a letter, a digit, an F-key, a named key, or one of ten
    // punctuation characters), while a SPACE can — `normalizeKeyName('A B')`
    // returns `'A B'` verbatim, so a space-joined `['A B']` collided with a
    // real `['A','B']`.
    const steps = binding.map((step) =>
      normalizeRegisterableHotkey(step, target),
    );
    return `seq:${steps.join('|')}`;
  }
  return `chord:${normalizeRegisterableHotkey(binding, target)}`;
}

/**
 * The chord a binding's FIRST keypress produces.
 *
 * A chord and a sequence live in separate `bindingKey` namespaces, which is
 * right for identity — `['G','H']` is not `G` — and wrong for conflicts. The
 * two managers are wholly independent (`HotkeyManager` and `SequenceManager`
 * each add their own `keydown` listener and neither knows about the other), so
 * binding `navigation.goHome` to a bare `G` while `goMeshgen` keeps `G M` makes
 * a single `G M` navigate home AND to MeshGen. Nothing suppresses one when the
 * other matches.
 *
 * Comparing first chords catches it. It is deliberately coarse: two sequences
 * sharing a prefix (`G H` and `G M`, which is the whole point of a prefix) must
 * NOT count, so `findKeymapConflicts` only compares first chords when at least
 * one side is a plain chord.
 */
function firstChord(binding: Binding, platform: Platform): string {
  const step = isSequenceBinding(binding) ? binding[0] : binding;
  if (step === undefined) return '';
  return normalizeRegisterableHotkey(step, platform);
}

/**
 * Whether two bindings would fight over a keypress.
 *
 * Identical bindings, or a chord that is some sequence's opening chord.
 */
function bindingsCollide(a: Binding, b: Binding, platform: Platform): boolean {
  if (bindingKey(a, platform) === bindingKey(b, platform)) return true;
  const aIsSequence = isSequenceBinding(a);
  const bIsSequence = isSequenceBinding(b);
  // Two sequences sharing an opening chord is a prefix, not a conflict.
  if (aIsSequence === bIsSequence) return false;
  return firstChord(a, platform) === firstChord(b, platform);
}

/** A binding that two or more commands in the same scope both claim. */
export interface KeymapConflict {
  /** The normalized binding identity — see `bindingKey`. */
  readonly bindingKey: string;
  /** The binding as first written, for display. */
  readonly binding: Binding;
  /**
   * The one scope every claimant shares, or `null` when the clash spans two.
   * A cross-scope row has no single scope to name, and naming the first
   * claimant's was actively misleading.
   */
  readonly scope: CommandScope | null;
  /** Every command claiming it, deduped, in catalog order. Length >= 1. */
  readonly commandIds: readonly CommandId[];
}

/**
 * Whether two commands sharing one chord is a problem worth reporting.
 *
 * Exactly one pair is exempt, and it is the one that makes scopes useful: a
 * leaf scope shadowing `'global'`. That is how `Escape` closes the
 * design-point detail pane rather than whatever `Escape` means at the top
 * level, and reporting it would put a permanent warning on the shipped
 * defaults and train users to ignore the list.
 *
 * Everything else conflicts, INCLUDING two different leaf scopes. Leaf scopes
 * are not mutually exclusive (see `CommandScope`) — `reportWriteup` is
 * co-active with `designPoints` for the whole analyze route — and a chord
 * shared between two co-active leaves would be arbitrated by mount order,
 * which no user can predict. Better to say so than to pick one silently.
 */
function scopesConflict(a: CommandScope, b: CommandScope): boolean {
  if (a === b) return true;
  return a !== 'global' && b !== 'global';
}

/**
 * Every double-binding in a keymap that `scopesConflict` counts.
 *
 * Used two ways: `keymap.test.ts` asserts the shipped defaults are clean, and
 * the shortcuts panel calls it on the resolved keymap so a user who records a
 * chord that is already taken sees it immediately.
 */
export function findKeymapConflicts(
  keymap: Keymap,
  platform?: Platform,
): readonly KeymapConflict[] {
  const target = platform ?? detectPlatform();

  // Flat list rather than a map keyed on the binding: `bindingsCollide` is not
  // an equality (a chord collides with a sequence that opens on it, while two
  // such sequences do not collide with each other), so it cannot be a hash key.
  // The catalog is a dozen commands, so the pairwise walk is free — and
  // insertion order stays catalog order, which is what makes the reported order
  // and `resolveBindings`' tie-break deterministic.
  const claims: Array<{
    binding: Binding;
    scope: CommandScope;
    id: CommandId;
  }> = [];
  for (const command of COMMANDS) {
    const scope = scopeOf(command);
    for (const binding of keymap[command.id] ?? []) {
      claims.push({ binding, scope, id: command.id });
    }
  }

  const conflicts: KeymapConflict[] = [];
  const reported = new Set<number>();
  claims.forEach((claim, index) => {
    if (reported.has(index)) return;
    const group = claims.filter(
      (other) =>
        scopesConflict(claim.scope, other.scope) &&
        bindingsCollide(claim.binding, other.binding, target),
    );
    // `group` always contains `claim` itself; two entries means a real clash.
    if (group.length < 2) return;
    claims.forEach((other, otherIndex) => {
      if (group.includes(other)) reported.add(otherIndex);
    });

    // Deduped: one command whose two aliases normalize to the same chord
    // contributes two claims, and reporting its name twice under a heading
    // that says "two commands" is simply wrong.
    const commandIds = [...new Set(group.map((member) => member.id))];
    const scopes = new Set(group.map((member) => member.scope));
    conflicts.push({
      bindingKey: bindingKey(claim.binding, target),
      binding: claim.binding,
      // `null` when the group spans two scopes: naming one of them sent the
      // reader looking for two Viewer commands on a key where only one lives.
      scope: scopes.size === 1 ? claim.scope : null,
      commandIds,
    });
  });
  return conflicts;
}

/**
 * The commands that already claim `binding` in `scope`, ignoring `exceptId`.
 *
 * The recorder in the shortcuts panel calls this with the chord the user just
 * pressed, so "⌘S is already Save report" can be said before the rebind is
 * committed rather than after.
 */
export function commandsClaiming(
  binding: Binding,
  keymap: Keymap,
  options: { scope: CommandScope; exceptId?: CommandId; platform?: Platform },
): readonly CommandId[] {
  const target = options.platform ?? detectPlatform();
  const claimants: CommandId[] = [];
  for (const command of COMMANDS) {
    if (command.id === options.exceptId) continue;
    // The same predicate `findKeymapConflicts` uses, so the recorder cannot
    // commit a pair that the dialog would then report as a conflict.
    if (!scopesConflict(scopeOf(command), options.scope)) continue;
    for (const candidate of keymap[command.id] ?? []) {
      // `bindingsCollide`, not key equality: rebinding a sequence command to a
      // bare `G` has to be reported against the `G M` sequence it would then
      // fire alongside.
      if (bindingsCollide(binding, candidate, target)) {
        claimants.push(command.id);
        break;
      }
    }
  }
  return claimants;
}

/**
 * The binding the UI should show for a command — the first one, or null when
 * the user has unbound it.
 */
export function primaryBinding(id: CommandId, keymap: Keymap): Binding | null {
  return keymap[id]?.[0] ?? null;
}

/**
 * Whether a binding is one this app is willing to register.
 *
 * Two rejections, both learned the hard way in every app that ships a
 * recorder: a bare modifier is not a chord (the recorder can emit one
 * mid-press), and a chord whose only key is `Tab` steals keyboard navigation
 * from anyone who does not use a mouse.
 */
export function isRegistrableBinding(binding: Binding): boolean {
  const steps = isSequenceBinding(binding) ? binding : [binding];
  if (steps.length === 0) return false;
  return steps.every((step) => {
    // `hasNonModifierKey` is the library's own answer to "is this a complete
    // chord", and the right one to defer to: an earlier hand-rolled version
    // here split on `+` and checked the last segment, which happily accepted a
    // bare `'Control'` because `'Control'` IS the last segment. A registered
    // bare modifier swallows the modifier for every chord built on it.
    if (!hasNonModifierKey(step)) return false;
    // Taking Tab, with or without modifiers, takes keyboard navigation away
    // from anyone who does not use a mouse.
    //
    // Via `parseHotkey` rather than a `split('+')` on the last segment, for the
    // same reason as the line above: the hand-rolled version accepted
    // `'Mod+tab'` and `'shift+tab'`, because the last segment is then the
    // lowercase `'tab'`. That path is reachable — `loadKeymapOverrides` feeds
    // user-editable localStorage strings straight into this function.
    if (parseHotkey(step).key === 'Tab') return false;
    // A key containing whitespace is not a key. `normalizeKeyName` passes an
    // unrecognized string through verbatim, so a hand-edited `[["A B"]]` would
    // otherwise register as a one-step sequence.
    if (/\s/.test(String(step))) return false;
    return true;
  });
}

/** Type guard for values arriving from storage or a URL. */
export function isCommandId(value: unknown): value is CommandId {
  return typeof value === 'string' && commandsById.has(value as CommandId);
}
